/**
 * AddressField — one entity address (registered or operational) rendered as a
 * compact multi-line block in read mode, expanding into the structured
 * sub-fields on edit.
 *
 * Read:  envelope-style lines composed from the structured columns; falls back
 *        to the legacy free-text (`registeredOffice` / `operationalAddress`)
 *        until the structured fields are filled.
 * Edit:  6 stacked inputs (line1, line2, city, state/province, postal, country),
 *        seeded from the current values or — when empty — a best-effort split of
 *        the legacy free-text (country pre-filled from the entity's own ISO2).
 *        Save PATCHes only the changed sub-fields via the generic entity PATCH.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { api, extractApiError } from "../lib/api";
import { cn } from "../lib/cn";
import { composeAddressLines, splitAddress, type AddressParts } from "../lib/addressFormat";
import type { Entity } from "../lib/types";
import { FieldActions } from "./FieldActions";

type Prefix = "registered" | "operational";

interface Props {
  entity: Entity;
  prefix: Prefix;
  label: string;
  canEdit: boolean;
}

const SUB_KEYS = ["Line1", "Line2", "City", "StateProvince", "PostalCode", "CountryCode"] as const;
type SubKey = (typeof SUB_KEYS)[number];

// Maps a sub-key to the actual Entity column for a given address prefix.
function col(prefix: Prefix, key: SubKey): keyof Entity {
  // registeredAddressLine1 / registeredCity / registeredCountryCode / ...
  if (key === "Line1") return `${prefix}AddressLine1` as keyof Entity;
  if (key === "Line2") return `${prefix}AddressLine2` as keyof Entity;
  return `${prefix}${key}` as keyof Entity;
}

type Draft = Record<SubKey, string>;

function partsFromEntity(entity: Entity, prefix: Prefix): AddressParts {
  return {
    line1: (entity[col(prefix, "Line1")] as string | null) ?? "",
    line2: (entity[col(prefix, "Line2")] as string | null) ?? "",
    city: (entity[col(prefix, "City")] as string | null) ?? "",
    stateProvince: (entity[col(prefix, "StateProvince")] as string | null) ?? "",
    postalCode: (entity[col(prefix, "PostalCode")] as string | null) ?? "",
    countryCode: (entity[col(prefix, "CountryCode")] as string | null) ?? "",
  };
}

export function AddressField({ entity, prefix, label, canEdit }: Props): JSX.Element {
  const qc = useQueryClient();
  const { t } = useTranslation(["entities", "common"]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({
    Line1: "", Line2: "", City: "", StateProvince: "", PostalCode: "", CountryCode: "",
  });

  const legacyKey: keyof Entity = prefix === "registered" ? "registeredOffice" : "operationalAddress";
  const legacyText = (entity[legacyKey] as string | null) ?? "";
  const parts = partsFromEntity(entity, prefix);

  // Country name only when the address country matches the entity's country.
  const countryName =
    parts.countryCode && entity.country?.iso2?.toUpperCase() === String(parts.countryCode).toUpperCase()
      ? entity.country?.name
      : null;
  const lines = composeAddressLines(parts, countryName);

  function openEdit(): void {
    const empty = lines.length === 0;
    if (empty) {
      const s = splitAddress(legacyText, entity.country?.iso2);
      setDraft({
        Line1: s.line1, Line2: s.line2, City: s.city,
        StateProvince: s.stateProvince, PostalCode: s.postalCode, CountryCode: s.countryCode,
      });
    } else {
      setDraft({
        Line1: parts.line1 ?? "", Line2: parts.line2 ?? "", City: parts.city ?? "",
        StateProvince: parts.stateProvince ?? "", PostalCode: parts.postalCode ?? "",
        CountryCode: parts.countryCode ?? "",
      });
    }
    setEditing(true);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      // Pre-flight: country code must be empty or exactly 2 letters (matches
      // backend validation). Catches the most likely failure path before any
      // sub-PATCH commits, avoiding the partial-save state where some fields
      // are written and a later one 422s.
      const cc = draft.CountryCode.trim();
      if (cc !== "" && !/^[A-Za-z]{2}$/.test(cc)) {
        throw new Error("invalid_country_code");
      }
      // PATCH only the sub-fields whose value changed.
      for (const key of SUB_KEYS) {
        const field = col(prefix, key);
        const next = draft[key].trim();
        const current = ((entity[field] as string | null) ?? "").trim();
        if (next === current) continue;
        await api.patch(`/entities/${entity.id}`, { field: field as string, value: next, comment: null });
      }
    },
    onSuccess: () => {
      toast.success(t("entities:address_saved", { defaultValue: "Address updated" }));
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ["entity", entity.id] });
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["entities-all"] });
      void qc.invalidateQueries({ queryKey: ["changelog", { entityId: entity.id }] });
      void qc.invalidateQueries({ queryKey: ["changelog-recent"] });
    },
    onError: (err) => {
      toast.error(extractApiError(err));
      // Invalidate so the UI re-reads server state — guards against the rare
      // partial-save case where one of the sub-PATCHes failed mid-loop and
      // earlier ones already committed.
      void qc.invalidateQueries({ queryKey: ["entity", entity.id] });
    },
  });

  const inputClass = cn(
    // `text-base` (16px) is required to prevent iOS auto-zoom on focus
    // (CLAUDE.md rule 7). Smaller fonts trigger Safari's zoom-on-focus
    // behaviour which then breaks the entity-detail layout.
    "text-base border border-border rounded-[6px] px-2 py-1 flex-1 min-w-0",
    "bg-surface-2 focus:bg-surface focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/14",
  );

  const subLabels: Record<SubKey, string> = {
    Line1: t("entities:address_line1", { defaultValue: "Address line 1" }),
    Line2: t("entities:address_line2", { defaultValue: "Address line 2" }),
    City: t("entities:address_city", { defaultValue: "City" }),
    StateProvince: t("entities:address_state", { defaultValue: "State / province" }),
    PostalCode: t("entities:address_postal", { defaultValue: "Postal code" }),
    CountryCode: t("entities:address_country", { defaultValue: "Country (ISO2)" }),
  };

  return (
    <div data-field={legacyKey as string} className="flex items-start gap-[14px] py-2 group">
      <span className="w-[148px] shrink-0 text-[13.5px] font-semibold text-text pt-0.5">{label}</span>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex flex-col gap-2 py-1">
            {SUB_KEYS.map((key) => (
              <label key={key} className="flex items-center gap-3">
                <span className="w-[120px] shrink-0 text-[11.5px] font-semibold uppercase tracking-[.03em] text-dim">
                  {subLabels[key]}
                </span>
                <input
                  type="text"
                  value={draft[key]}
                  maxLength={key === "CountryCode" ? 2 : undefined}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      [key]: key === "CountryCode" ? e.target.value.toUpperCase() : e.target.value,
                    }))
                  }
                  aria-label={subLabels[key]}
                  className={cn(inputClass, key === "CountryCode" && "flex-none w-[64px] uppercase")}
                />
              </label>
            ))}
            <div className="flex items-center gap-2 pl-[132px]">
              <button
                type="button"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate()}
                className={cn(
                  "inline-flex items-center gap-1 rounded-lg bg-accent px-3 text-[12.5px] font-semibold text-white",
                  "hover:bg-[#4338ca] disabled:opacity-50 min-h-[44px] sm:min-h-[34px]",
                  "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
                )}
              >
                <Check className="h-3.5 w-3.5" aria-hidden /> {t("common:save")}
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className={cn(
                  "inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-3 text-[12.5px] font-semibold text-text",
                  "hover:bg-surface-2 min-h-[44px] sm:min-h-[34px]",
                  "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20",
                )}
              >
                <X className="h-3.5 w-3.5" aria-hidden /> {t("common:cancel")}
              </button>
            </div>
          </div>
        ) : (
          <div className="group flex items-start gap-2">
            <div className="flex-1 min-w-0 text-[14px] text-text leading-relaxed">
              {lines.length > 0 ? (
                lines.map((l, i) => <div key={i}>{l}</div>)
              ) : legacyText.trim() ? (
                <span className="whitespace-pre-line">{legacyText}</span>
              ) : (
                <span className="text-dim font-normal">—</span>
              )}
            </div>
            <FieldActions fieldLabel={label} canEdit={canEdit} showClock={false} onEdit={openEdit} />
          </div>
        )}
      </div>
    </div>
  );
}
